import * as React from "react";
import { useMsal } from "@azure/msal-react";
import { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import { InteractionStatus } from "@azure/msal-browser";
import Loader from "../components/Loader";
import { apiClient, authApiClient, graphApiClient, ideasbankApiClient, pg3ApiClient } from "../api";
import { tokenRequest } from "../auth/config";
import {
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { logoutIntercom } from "src/services/intercom";


type ApiProviderProps = {
  children: React.ReactNode;
};

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

const clients = [apiClient, authApiClient, graphApiClient, ideasbankApiClient,pg3ApiClient];
let tokenRefreshPromise: Promise<string | null> | null = null;

const getAccessToken = async (
  instance: ReturnType<typeof useMsal>["instance"],
  account: ReturnType<typeof useMsal>["accounts"][number] | undefined,
): Promise<string | null> => {
  if (!account) {
    console.warn(" No active account found");
    return null;
  }

  if (tokenRefreshPromise) {
   
    return tokenRefreshPromise;
  }

  tokenRefreshPromise = instance
    .acquireTokenSilent({
      ...tokenRequest,
      account,
    })
    .then((response) => {
      localStorage.setItem(
        "accessToken",
        response.accessToken
      );

      return response.accessToken;
    })
.catch(async (error) => {
  console.log("Session expired. Logging out.",error);
  tokenRefreshPromise = null;
  logoutIntercom();
  localStorage.clear();
  await instance.logoutRedirect({
    postLogoutRedirectUri: window.location.origin,
  });

  return null;
})
.finally(() => {
  tokenRefreshPromise = null;
});
return tokenRefreshPromise;
};

const applyAuthorizationHeader = (
  config: InternalAxiosRequestConfig,
  token: string,
): InternalAxiosRequestConfig => {

  config.headers = {
    ...(config.headers ?? {}),
    Authorization: `Bearer ${token}`,
  } as InternalAxiosRequestConfig["headers"];
  return config;
};

// Validate token before use - check if expired or expiring soon
const validateAndRefreshToken = async (
  token: string | null,
  instance: ReturnType<typeof useMsal>["instance"],
  account: ReturnType<typeof useMsal>["accounts"][number] | undefined,
): Promise<string | null> => {
  if (!token) {
    return getAccessToken(instance, account);
  }
  return token;
};

export const ApiProvider = ({ children }: ApiProviderProps) => {
  const { instance, accounts, inProgress } = useMsal();
  const [isReady, setIsReady] = React.useState(false);

React.useEffect(() => {
  let isMounted = true;

  const cleanupHandlers: Array<() => void> = [];

  const setupInterceptors = () => {
    for (const client of clients) {
      const requestInterceptorId =
        client.client.interceptors.request.use(
          async (config) => {
            try {
              const account =
                instance.getActiveAccount() ??
                instance.getAllAccounts()[0];

              if (!account) {
                return config;
              }

              const token = await getAccessToken(
                instance,
                account
              );

              if (!token) {
                return config;
              }

              return applyAuthorizationHeader(
                config,
                token
              );
            } catch (err) {
              console.error(err);
              return config;
            }
          }
        );

      const responseInterceptorId =
        client.client.interceptors.response.use(
          (response) => response,
          async (error) => {
            const axiosError =
              error as AxiosError;

            const originalRequest =
              axiosError.config as RetryableRequestConfig;

            if (
              !originalRequest ||
              originalRequest._retry ||
              axiosError.response?.status !== 401
            ) {
              return Promise.reject(error);
            }

            try {
              const account =
                instance.getActiveAccount() ??
                instance.getAllAccounts()[0];

              if (!account) {
                return Promise.reject(error);
              }

              const token =
                await getAccessToken(
                  instance,
                  account
                );

              if (!token) {
                return Promise.reject(error);
              }

              originalRequest._retry = true;

              applyAuthorizationHeader(
                originalRequest,
                token
              );

              return client.client.request(
                originalRequest
              );
            } catch {
              return Promise.reject(error);
            }
          }
        );

      cleanupHandlers.push(() =>
        client.client.interceptors.request.eject(
          requestInterceptorId
        )
      );

      cleanupHandlers.push(() =>
        client.client.interceptors.response.eject(
          responseInterceptorId
        )
      );
    }

    if (isMounted) {
      setIsReady(true);
    }
  };

  setupInterceptors();

  return () => {
    isMounted = false;
    cleanupHandlers.forEach((c) => c());
  };
}, [instance]);
 
if (
  inProgress === InteractionStatus.Startup
) {
  return React.createElement(Loader, {
      message: "Preparing application...",
      subMessage: "",
    });
}
  return React.createElement(React.Fragment, null, children);
};

export default ApiProvider;
